import React, { useState, useRef, useEffect } from 'react';
import { supabaseAdmin as supabase } from '../../lib/supabase.js';
import TimetableSetup from './TimetableSetup';
import { EnhancedTimetableParser } from './EnhancedTimetableParser';
import TimetableGrid from './TimetableGrid';
import TimetableSettings from './TimetableSettings';
import AppSetupManager from '../Setup/AppSetupManager';
// Debug components removed - Debug directory was deleted during migration
// import TimetableDataViewer from '../Debug/TimetableDataViewer';
// import TestTimetableData from '../Debug/TestTimetableData';
// import DataStructureTest from '../Debug/DataStructureTest';
import './styles/ModernButtons.css';

const tenantId = 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3';

const TimetableManagement = ({ teacherId, teacherName, onTimetableUpdated }) => {
  console.log('🚀 TimetableManagement component rendered');
  console.log('🚀 Props received:', { teacherId, teacherName });
  
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [uploadedData, setUploadedData] = useState(null);
  const [editingMode, setEditingMode] = useState(false);
  const [editableTimetable, setEditableTimetable] = useState(null);
  const [showSetup, setShowSetup] = useState(false);
  const [showAppSetup, setShowAppSetup] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [timetableConfig, setTimetableConfig] = useState(null);
  const [parser, setParser] = useState(null);
  const [organizedTimetableData, setOrganizedTimetableData] = useState(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const fileInputRef = useRef(null);

  // Organize timetable data when it's loaded
  useEffect(() => {
    if (editableTimetable) {
      console.log('📊 Organizing timetable data:', editableTimetable);
      const organized = organizeTimetableData(editableTimetable);
      console.log('📊 Organized timetable data:', organized);
      setOrganizedTimetableData(organized);
    }
  }, [editableTimetable]);

  const showMessage = (text, type = 'info') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 5000);
  };

  // Handle cell edit from TimetableGrid
  const handleCellEdit = (day, period, editData) => {
    console.log('📝 Cell edit:', { day, period, editData });
    
    if (!editableTimetable) return;
    
    // Update the editable timetable data
    const updatedTimetable = { ...editableTimetable };
    if (updatedTimetable[day] && updatedTimetable[day][period]) {
      updatedTimetable[day][period] = {
        ...updatedTimetable[day][period],
        ...editData
      };
      
      setEditableTimetable(updatedTimetable);
      showMessage('時間表項目已更新', 'success');
    }
  };

  // Get teacher ID by name
  const getTeacherIdByName = async (teacherName) => {
    try {
      console.log(`🔍 getTeacherIdByName called with: "${teacherName}"`);
      console.log(`🏢 Using tenantId: ${tenantId}`);
      
      if (!teacherName || teacherName === 'Unknown Teacher') {
        console.log(`❌ Invalid teacher name: "${teacherName}"`);
        return null;
      }

      console.log(`🔍 Querying saas_teachers for: "${teacherName}"`);
      // Try exact match first
      let { data, error } = await supabase
        .from('saas_teachers')
        .select('id, name, code, teacher_id')
        .eq('tenant_id', tenantId)
        .eq('status', 'active')
        .eq('name', teacherName);
      
      // If no exact match, try partial match
      if (!data || data.length === 0) {
        console.log(`🔍 No exact match, trying partial match for: "${teacherName}"`);
        const partialResult = await supabase
          .from('saas_teachers')
          .select('id, name, code, teacher_id')
          .eq('tenant_id', tenantId)
          .eq('status', 'active')
          .ilike('name', `%${teacherName}%`);
        
        data = partialResult.data;
        error = partialResult.error;
      }
      
      // If still no match, try code match
      if (!data || data.length === 0) {
        console.log(`🔍 No partial match, trying code match for: "${teacherName}"`);
        const codeResult = await supabase
          .from('saas_teachers')
          .select('id, name, code, teacher_id')
          .eq('tenant_id', tenantId)
          .eq('status', 'active')
          .eq('code', teacherName);
        
        data = codeResult.data;
        error = codeResult.error;
      }

      if (error) {
        console.error('❌ Error looking up teacher:', error);
        return null;
      }

      console.log(`📊 Query result for "${teacherName}":`, data);
      
      if (data && data.length > 0) {
        console.log(`✅ Found teacher ${teacherName}:`, data[0]);
        console.log(`📋 Returning teacher_id: ${data[0].teacher_id}`);
        return data[0].teacher_id; // Return teacher_id instead of id
      }

      console.warn(`❌ Teacher not found: ${teacherName}`);
      return null;
    } catch (error) {
      console.error('❌ Error in getTeacherIdByName:', error);
      return null;
    }
  };

  const scrollLeft = () => {
    setScrollPosition(prev => Math.max(0, prev - 150));
  };

  const scrollRight = () => {
    setScrollPosition(prev => Math.min(600, prev + 150));
  };

  const resetScroll = () => {
    setScrollPosition(0);
  };

  // Load timetable setup on component mount
  useEffect(() => {
    loadTimetableSetup();
    loadTimetableConfig();
  }, []);

  const loadTimetableConfig = async () => {
    try {
      const { data, error } = await supabase
        .from('timetable_configurations')
        .select('config')
        .eq('academic_year', '2024-2025')
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading timetable config:', error);
        return;
      }

      if (data) {
        setTimetableConfig(data.config);
        console.log('Loaded timetable configuration:', data.config);
      }
    } catch (error) {
      console.error('Error loading timetable config:', error);
    }
  };

  // Initialize parser when setup data is loaded
  useEffect(() => {
    if (setupData) {
      // Ensure we have period patterns, use default if none exist
      const setupDataWithPatterns = {
        ...setupData,
        periodPatterns: setupData.periodPatterns && setupData.periodPatterns.length > 0 
          ? setupData.periodPatterns 
          : [
              { period_sequence: 1, period_node: '班主任節', time_slot: '8:00-8:10', period_number: 0 },
              { period_sequence: 2, period_node: 'Period 1', time_slot: '8:10-8:45', period_number: 1 },
              { period_sequence: 3, period_node: 'Period 2', time_slot: '8:45-9:20', period_number: 2 },
              { period_sequence: 4, period_node: 'Period 3', time_slot: '9:20-9:55', period_number: 3 },
              { period_sequence: 5, period_node: 'Recess', time_slot: '9:55-10:15', period_number: 0 },
              { period_sequence: 6, period_node: 'Period 4', time_slot: '10:15-10:50', period_number: 4 },
              { period_sequence: 7, period_node: 'Period 5', time_slot: '10:50-11:25', period_number: 5 },
              { period_sequence: 8, period_node: 'Recess', time_slot: '11:25-11:30', period_number: 0 },
              { period_sequence: 9, period_node: 'Period 6', time_slot: '11:30-12:05', period_number: 6 },
              { period_sequence: 10, period_node: 'Period 7', time_slot: '12:05-12:40', period_number: 7 },
              { period_sequence: 11, period_node: 'Lunch', time_slot: '12:40-1:50', period_number: 0 },
              { period_sequence: 12, period_node: 'Period 8', time_slot: '1:50-2:25', period_number: 8 },
              { period_sequence: 13, period_node: 'Period 9', time_slot: '2:25-3:00', period_number: 9 },
              { period_sequence: 14, period_node: 'Period 10', time_slot: '3:00-3:35', period_number: 10 },
              { period_sequence: 15, period_node: '班主任節', time_slot: '3:35-3:50', period_number: 0 }
            ]
      };
      
      console.log('Initializing parser with setup data:', setupDataWithPatterns);
      const enhancedParser = new EnhancedTimetableParser(setupDataWithPatterns);
      setParser(enhancedParser);
    }
  }, [setupData]);

  const loadTimetableSetup = async () => {
    try {
      // STRICT RULE: Load from timetable_configurations table (settings page)
      console.log('🔍 Loading timetable configuration from settings...');
      
        const { data, error } = await supabase
        .from('timetable_configurations')
          .select('*')
        .eq('academic_year', '2024-2025')
          .single();

      if (error) {
        console.error('❌ Failed to load timetable configuration:', error);
        throw new Error('Please configure timetable settings first!');
      }

      if (data && data.config) {
        console.log('✅ Loaded timetable configuration from settings:', data.config);
        
        const setupData = {
            mode: 'weekday',
          dayPeriods: data.config.total_periods || 15,
          periodPatterns: data.config.time_slots || [],
          day_specific_overrides: data.config.day_specific_overrides || {}
        };
        
        console.log('✅ Loaded setup data from settings:', setupData);
        setSetupData(setupData);
        return;
      } else {
        throw new Error('No timetable configuration found! Please configure settings first.');
      }
    } catch (error) {
      console.error('❌ Failed to load timetable configuration:', error);
      throw new Error('Please configure timetable settings first! Go to Settings > Timetable Settings.');
    }
  };

  const handleSetupComplete = (newSetupData) => {
    setSetupData(newSetupData);
    setShowSetup(false);
    showMessage('Timetable setup updated successfully!', 'success');
  };

  const handleAppSetupComplete = () => {
    setShowAppSetup(false);
    showMessage('App setup completed! Refreshing timetable setup...', 'success');
    // Reload timetable setup after app setup is complete
    loadTimetableSetup();
  };

  // Enhanced file parsing using the new parser
  const parseTimetableFile = (content) => {
    // STRICT RULE: Use settings configuration, NO fallback
    if (!parser) {
      throw new Error('Parser not initialized! Please configure timetable settings first.');
    }
    
    try {
      const parsedData = parser.parseTimetableFile(content);
      return parsedData;
    } catch (error) {
      console.error('Error parsing file:', error.message);
      throw error;
    }
  };

  // Validation function based on TIMETABLE_LOGIC.md
  const validatePeriodNode = (period_sequence, period_node, dayName) => {
    console.log(`🔍 Validating ${dayName} period ${period_sequence}: ${period_node}`);
    
    // No special case for Period 10 (sequence 14) - it should be Period 10 for all days
    
    // Standard special periods (same for all days)
    const expectedNodes = {
      1: '班主任節',    // First 班主任節
      5: 'Recess', 
      8: 'Recess',
      11: 'Lunch'
      // Note: Period 15 (班主任節) only applies to Friday, handled separately
    };
    
    if (expectedNodes[period_sequence] && period_node !== expectedNodes[period_sequence]) {
      console.warn(`⚠️ ${dayName} Period sequence ${period_sequence} should be ${expectedNodes[period_sequence]}, correcting from ${period_node}`);
      return expectedNodes[period_sequence];
    }
    
    // Friday special case: Period 15 (sequence 15) is 班主任節
    if (dayName === 'Friday' && period_sequence === 15) {
      if (period_node !== '班主任節') {
        console.warn(`⚠️ Friday Period 15 (sequence 15) MUST be 班主任節, correcting from ${period_node}`);
        return '班主任節';
      }
    } else if (dayName !== 'Friday' && period_sequence === 15) {
      // Non-Friday Period 15 should be empty or different
      if (period_node === '班主任節') {
        console.warn(`⚠️ ${dayName} Period 15 (sequence 15) should NOT be 班主任節, correcting from ${period_node}`);
        return 'Free Period';
      }
    }
    
    return period_node;
  };

  const convertToTimetableGridFormat = (parsedData) => {
    // Create 15-period structure for each day
    const gridData = {
      monday: new Array(15).fill(null),
      tuesday: new Array(15).fill(null),
      wednesday: new Array(15).fill(null),
      thursday: new Array(15).fill(null),
      friday: new Array(15).fill(null)
    };
    
    // Day mapping
    const dayMapping = {
      'Monday': 'monday',
      'Tuesday': 'tuesday', 
      'Wednesday': 'wednesday',
      'Thursday': 'thursday',
      'Friday': 'friday'
    };
    
    // Get the default teacher name from parsed data
    const defaultTeacher = parsedData.teacherName || 'Unknown Teacher';
    console.log(`👨‍🏫 Using default teacher: ${defaultTeacher}`);
    
    // Process each day's schedule
    const schedule = parsedData.schedule || parsedData;
    if (schedule) {
      for (const [dayName, periods] of Object.entries(schedule)) {
        const dayKey = dayMapping[dayName];
        if (!dayKey || !Array.isArray(periods)) continue;
        
        for (const period of periods) {
          if (!period || !period.period_sequence) continue;
          
          // Debug: Log the period data structure
          console.log(`🔍 Processing ${dayName} period:`, {
            period_sequence: period.period_sequence,
            subject: period.subject,
            classes: period.classes,
            rooms: period.rooms,
            teachers: period.teachers,
            type: period.type
          });
          
          // Place entry at correct period_sequence index (0-based)
          const arrayIndex = period.period_sequence - 1;
          if (arrayIndex >= 0 && arrayIndex < 15) {
            // Validate and correct period_node for special periods
            const validatedPeriodNode = validatePeriodNode(period.period_sequence, period.period_node, dayName);
            
            // Extract just the subject name from the full description
            let subjectName = 'Unknown Subject';
            if (period.subject) {
              // Remove class info, room info, and teacher info to get just the subject
              let cleanSubject = period.subject
                .replace(/^[0-9]+[A-Za-z]+\s+/, '') // Remove class (e.g., "3C ")
                .replace(/Room\s+[A-Za-z0-9\/\s]+/, '') // Remove room info
                .replace(/\s+[A-Za-z\u4e00-\u9fff]+$/, '') // Remove teacher name at end
                .replace(/^核心科\s+/, '') // Remove "核心科 " prefix
                .replace(/^早會\/班主任節.*/, '班主任節') // Handle special periods
                .trim();
              
              subjectName = cleanSubject || 'Unknown Subject';
            }

            const gridEntry = {
              period_sequence: period.period_sequence,
              period_node: validatedPeriodNode,
              period_number: period.period_number || (period.period_sequence <= 4 ? period.period_sequence - 1 : period.period_sequence <= 7 ? period.period_sequence - 2 : period.period_sequence <= 10 ? period.period_sequence - 3 : period.period_sequence <= 14 ? period.period_sequence - 4 : 0),
              time_slot: period.time_slot,
              subject: subjectName,
              class_id: period.classes && period.classes.length > 0 ? period.classes[0] : 'Unknown Class',
              classroom: period.rooms && period.rooms.length > 0 ? period.rooms[0] : 'Unknown Room',
              teacher: period.teachers && period.teachers.length > 0 ? period.teachers[0] : defaultTeacher,
              type: period.type || 'core'
            };
            
            gridData[dayKey][arrayIndex] = gridEntry;
            
            // Log validation results with day context
            console.log(`✅ ${dayName} Period ${period.period_sequence}: ${validatedPeriodNode} - ${gridEntry.subject} (${gridEntry.class_id}) in ${gridEntry.classroom} with ${gridEntry.teacher}`);
          }
        }
      }
    }
    
    // Log conversion summary
    console.log('📊 CONVERSION SUMMARY:');
    Object.keys(gridData).forEach(day => {
      const nonNullEntries = gridData[day].filter(entry => entry !== null);
      console.log(`${day}: ${nonNullEntries.length}/15 periods filled`);
      nonNullEntries.forEach(entry => {
        console.log(`  - Period ${entry.period_sequence}: ${entry.period_node} - ${entry.subject || 'No subject'} (${entry.class_id || 'No class'})`);
      });
    });
    
    return gridData;
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.txt')) {
      showMessage('Please upload a .txt file', 'error');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target.result;
        console.log('File content loaded, length:', content.length);
        
        const parsedData = parseTimetableFile(content);
        console.log('🔍 PARSED DATA STRUCTURE:', parsedData);
        console.log('🔍 Parsed data keys:', Object.keys(parsedData));
        console.log('🔍 Schedule data:', parsedData.schedule);
        console.log('🔍 Sample Monday periods:', parsedData.schedule?.Monday?.slice(0, 3));
        
        // Debug: Check the structure of a specific period
        if (parsedData.schedule?.Monday?.length > 0) {
          const firstPeriod = parsedData.schedule.Monday[0];
          console.log('🔍 First Monday period structure:', firstPeriod);
          console.log('🔍 First period classes:', firstPeriod.classes);
          console.log('🔍 First period subject:', firstPeriod.subject);
          console.log('🔍 First period rooms:', firstPeriod.rooms);
          console.log('🔍 First period teachers:', firstPeriod.teachers);
        }
        
        // Convert parsed data to TimetableGrid format
        console.log('🔍 Raw parsed data structure:', parsedData);
        console.log('🔍 Parsed data schedule:', parsedData.schedule);
        if (parsedData.schedule) {
          console.log('🔍 Schedule keys:', Object.keys(parsedData.schedule));
          for (const [day, periods] of Object.entries(parsedData.schedule)) {
            console.log(`🔍 ${day} periods:`, periods);
            console.log(`🔍 ${day} period count:`, periods ? periods.length : 0);
          }
        }
        
        const gridData = convertToTimetableGridFormat(parsedData);
        console.log('Grid data converted:', gridData);
        console.log('Sample Monday data:', gridData.monday.slice(0, 5));
        console.log('Non-null entries in Monday:', gridData.monday.filter(entry => entry !== null).length);
        
        setUploadedData(gridData);
        setEditableTimetable(gridData);
        setEditingMode(true);
        setShowFileUpload(false);
        
        // Debug: Check if editableTimetable is set
        console.log('✅ Setting editableTimetable to:', gridData);
        console.log('✅ editableTimetable state should now contain data');
        
        console.log('✅ PARSING COMPLETED SUCCESSFULLY!');
        console.log('✅ editingMode should now be true');
        showMessage('File uploaded and parsed successfully! You can now edit the timetable.', 'success');
      } catch (error) {
        console.error('File upload error:', error);
        showMessage(`Error parsing file: ${error.message}`, 'error');
      }
    };
    reader.readAsText(file);
  };

  // Helper function to get time slot by period sequence
  const getTimeSlotByPeriodSequence = (periodSequence) => {
    const timeSlots = [
      '8:00-8:10',   // 1 - 班主任節
      '8:10-8:45',   // 2 - Period 1
      '8:45-9:20',   // 3 - Period 2
      '9:20-9:55',   // 4 - Period 3
      '9:55-10:15',  // 5 - Recess
      '10:15-10:50', // 6 - Period 4
      '10:50-11:25', // 7 - Period 5
      '11:25-11:30', // 8 - Recess
      '11:30-12:05', // 9 - Period 6
      '12:05-12:40', // 10 - Period 7
      '12:40-1:50',  // 11 - Lunch
      '1:50-2:25',   // 12 - Period 8
      '2:25-3:00',   // 13 - Period 9
      '3:00-3:35',   // 14 - Period 10
      '3:35-3:50'    // 15 - 班主任節
    ];
    return timeSlots[periodSequence - 1] || '8:00-8:10';
  };

  // Helper function to calculate period fields based on period sequence
  const calculatePeriodFields = (periodSequence) => {
    let periodNode, periodNumber;
    
    if (periodSequence === 1) {
      periodNode = '班主任節';
      periodNumber = 0;
    } else if (periodSequence === 2) {
      periodNode = 'Period 1';
      periodNumber = 1;
    } else if (periodSequence === 3) {
      periodNode = 'Period 2';
      periodNumber = 2;
    } else if (periodSequence === 4) {
      periodNode = 'Period 3';
      periodNumber = 3;
    } else if (periodSequence === 5) {
      periodNode = 'Recess';
      periodNumber = 0;
    } else if (periodSequence === 6) {
      periodNode = 'Period 4';
      periodNumber = 4;
    } else if (periodSequence === 7) {
      periodNode = 'Period 5';
      periodNumber = 5;
    } else if (periodSequence === 8) {
      periodNode = 'Recess';
      periodNumber = 0;
    } else if (periodSequence === 9) {
      periodNode = 'Period 6';
      periodNumber = 6;
    } else if (periodSequence === 10) {
      periodNode = 'Period 7';
      periodNumber = 7;
    } else if (periodSequence === 11) {
      periodNode = 'Lunch';
      periodNumber = 0;
    } else if (periodSequence === 12) {
      periodNode = 'Period 8';
      periodNumber = 8;
    } else if (periodSequence === 13) {
      periodNode = 'Period 9';
      periodNumber = 9;
    } else if (periodSequence === 14) {
      periodNode = 'Period 10';
      periodNumber = 10;
    } else if (periodSequence === 15) {
      periodNode = '班主任節';
      periodNumber = 0;
    } else {
      // Fallback for invalid period sequences
      periodNode = `Period ${periodSequence}`;
      periodNumber = periodSequence;
    }
    
    return { periodNode, periodNumber };
  };

  // Helper function to organize timetable data by period_sequence
  const organizeTimetableData = (rawData) => {
    const organizedData = {
      monday: new Array(15).fill(null),
      tuesday: new Array(15).fill(null),
      wednesday: new Array(15).fill(null),
      thursday: new Array(15).fill(null),
      friday: new Array(15).fill(null)
    };

    const dayMapping = {
      'Monday': 'monday',
      'Tuesday': 'tuesday',
      'Wednesday': 'wednesday',
      'Thursday': 'thursday',
      'Friday': 'friday'
    };

    if (rawData && Array.isArray(rawData)) {
      rawData.forEach(entry => {
        const dayKey = dayMapping[entry.day_of_week];
        if (dayKey && entry.period_sequence && entry.period_sequence >= 1 && entry.period_sequence <= 15) {
          // Place the entry at the correct index (period_sequence - 1)
          organizedData[dayKey][entry.period_sequence - 1] = {
            subject: entry.subject || '',
            class_id: entry.class_id || '',
            classroom: entry.classroom || '',
            notes: entry.notes || '',
            period_sequence: entry.period_sequence,
            period_node: entry.period_node,
            period_number: entry.period_number,
            time_slot: entry.time_slot,
            week_type: entry.week_type || 'regular',
            type: entry.subject_type || 'core',
            elective_group: entry.elective_group,
            id: entry.id || `${entry.day_of_week}_${entry.period_sequence}_${Date.now()}`
          };
        }
      });
    }

    return organizedData;
  };

  const handleEditCell = (day, index, field, value) => {
    if (!editableTimetable) return;
    
    const updatedTimetable = { ...editableTimetable };
    
    // Ensure the day array exists and is long enough
    if (!updatedTimetable[day]) {
      updatedTimetable[day] = [];
    }
    
    // Ensure the array is long enough for the index
    while (updatedTimetable[day].length <= index) {
      updatedTimetable[day].push(null);
    }
    
    // If the entry doesn't exist, create a new one
    if (!updatedTimetable[day][index]) {
      updatedTimetable[day][index] = {
        subject: '',
        class_id: '',
        classroom: '',
        notes: '',
        period_sequence: index + 1,
        period_node: `Period ${index + 1}`,
        time_slot: '8:00-8:10' // Default time slot
      };
    }
    
    // Update the field
    updatedTimetable[day][index] = {
      ...updatedTimetable[day][index],
      [field]: value
    };
    
    setEditableTimetable(updatedTimetable);
  };

  const handleSaveEditedTimetable = async () => {
    // Clear console for easier debugging
    console.clear();
    console.log('🔥🔥🔥 GREEN SAVE BUTTON CLICKED! 🔥🔥🔥');
    console.log('🔍 Current editableTimetable state:', editableTimetable);
    console.log('🔍 editableTimetable type:', typeof editableTimetable);
    console.log('🔍 editableTimetable keys:', editableTimetable ? Object.keys(editableTimetable) : 'N/A');
    console.log('🔍 teacherId:', teacherId);
    console.log('🔍 tenantId:', tenantId);
    
    // Basic test to see if function is being called - REMOVED ALERT
    
    if (!editableTimetable) {
      console.log('❌ No editableTimetable data');
      return;
    }
    
    console.log('✅ editableTimetable data exists, continuing...');
    
    // Validate required props
    if (!teacherId) {
      console.log('❌ No teacherId provided');
      showMessage('Teacher ID is required for saving', 'error');
      return;
    }
    
    if (!tenantId) {
      console.log('❌ No tenantId provided');
      showMessage('Tenant ID is required for saving', 'error');
      return;
    }
    
    console.log('✅ All required props validated, starting save process...');
    
    // SIMPLE TEST: Try to save one test record first
    console.log('🧪 TESTING: Saving one test record...');
    const testRecord = {
      tenant_id: tenantId,
      teacher_id: teacherId,
      teacher_name: teacherName,
      day_of_week: 'Monday',
      period_sequence: 2,
      period_node: 'Period 1',
      period_number: 1,
      time_slot: '8:10-8:45',
      subject: 'Test Subject',
      class_id: '1A',
      classroom: 'Test Room',
      subject_type: 'core', // Add subject_type field
      elective_group: null, // Add elective_group field
      // co_teachers: null, // Temporarily disabled - column doesn't exist in DB
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    console.log('📋 Test record to insert:', testRecord);
    console.log('🔍 Supabase client:', supabase);
    
    try {
      console.log('🚀 Starting database insert...');
      const { data, error } = await supabase
        .from('saas_teacher_timetables')
        .insert([testRecord])
        .select();
        
      console.log('📊 Insert result - data:', data);
      console.log('📊 Insert result - error:', error);
        
      if (error) {
        console.error('❌ Test insert error:', error);
        alert('Test insert failed: ' + error.message);
        showMessage('Test insert failed: ' + error.message, 'error');
        return;
      } else {
        console.log('✅ Test insert success:', data);
        alert('Test record saved successfully!');
        showMessage('Test record saved successfully!', 'success');
      }
    } catch (error) {
      console.error('❌ Test insert exception:', error);
      alert('Test insert exception: ' + error.message);
      showMessage('Test insert exception: ' + error.message, 'error');
        return;
      }

    console.log('🚀🚀🚀 STARTING MAIN SAVE PROCESS 🚀🚀🚀');
    console.log('Starting save process with:', { teacherId, tenantId, teacherName });
    console.log('Editable timetable data:', editableTimetable);
    console.log('Editable timetable keys:', Object.keys(editableTimetable));
    
    // DEBUG: Check if we have any data at all
    if (editableTimetable) {
      console.log('🔍 DEBUG: Checking editableTimetable structure...');
      Object.keys(editableTimetable).forEach(day => {
        if (editableTimetable[day]) {
          const nonNullEntries = editableTimetable[day].filter(entry => entry !== null);
          console.log(`🔍 ${day}: ${nonNullEntries.length} non-null entries`);
          if (nonNullEntries.length > 0) {
            console.log(`🔍 Sample ${day} entry:`, nonNullEntries[0]);
          }
        }
      });
    } else {
      console.log('❌ editableTimetable is null or undefined!');
      showMessage('No timetable data to save', 'error');
      return;
    }
    
    console.log('✅✅✅ DATA STRUCTURE CHECK COMPLETE ✅✅✅');
    
    try {
      console.log('🔄🔄🔄 SETTING LOADING STATE TO TRUE 🔄🔄🔄');
      setLoading(true);
      
      console.log('📝📝📝 STARTING DATA PROCESSING 📝📝📝');
      // Convert UI data to database format and collect teacher IDs
      const newRecords = [];
      const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
      const dayMapping = {
        'monday': 'Monday',
        'tuesday': 'Tuesday',
        'wednesday': 'Wednesday',
        'thursday': 'Thursday',
        'friday': 'Friday'
      };

      console.log('🗂️🗂️🗂️ INITIALIZING DATA STRUCTURES 🗂️🗂️🗂️');
      // Process each day and collect all entries with their teacher lookups
      const entriesWithTeacherIds = [];
      const teacherIdsToDelete = new Set();
      
      console.log('📊📊📊 STARTING DAY PROCESSING LOOP 📊📊📊');
      
      for (const day of days) {
        console.log(`Processing ${day}:`, editableTimetable[day]);
        if (editableTimetable[day]) {
          console.log(`🔍 ${day} has ${editableTimetable[day].length} entries`);
          for (let index = 0; index < editableTimetable[day].length; index++) {
            const entry = editableTimetable[day][index];
            
            // Skip null entries
            if (!entry) {
              console.log(`Skipping null entry at ${day}[${index}]`);
              continue;
            }
            
            console.log(`🔍 Processing entry ${index} for ${day}:`, entry);
            
            // Use the actual parsed data from the entry, not calculated values
            const periodSequence = entry.period_sequence || (index + 1);
            const periodNode = entry.period_node || calculatePeriodFields(periodSequence).periodNode;
            const periodNumber = entry.period_number !== undefined ? entry.period_number : calculatePeriodFields(periodSequence).periodNumber;
            const timeSlot = entry.time_slot || entry.time || getTimeSlotByPeriodSequence(periodSequence);
            
            console.log(`Saving entry for ${day} at index ${index}: period_sequence=${periodSequence}, period_node=${periodNode}, period_number=${periodNumber}, time_slot=${timeSlot}`, entry);
            
            // Extract class information from the grid data
            const classId = entry.class_id || '';
            const classroom = entry.classroom || '';
            const parsedTeachers = entry.teachers || [entry.teacher] || [];
            
            // Extract just the subject name from the full description
            let subjectName = 'Unknown Subject';
            if (entry.subject) {
              // Remove class info, room info, and teacher info to get just the subject
              let cleanSubject = entry.subject
                .replace(/^[0-9]+[A-Za-z]+\s+/, '') // Remove class (e.g., "3C ")
                .replace(/Room\s+[A-Za-z0-9\/\s]+/, '') // Remove room info
                .replace(/\s+[A-Za-z\u4e00-\u9fff]+$/, '') // Remove teacher name at end
                .replace(/^核心科\s+/, '') // Remove "核心科 " prefix
                .replace(/^早會\/班主任節.*/, '班主任節') // Handle special periods
                .trim();
              
              subjectName = cleanSubject || 'Unknown Subject';
            }
            
            // No need to truncate - database columns are now large enough
            const safeClassId = classId || '';
            const safeClassroom = classroom || '';
            const safeTeacherName = teacherName || '';
            const safeTimeSlot = timeSlot || '';
            const safePeriodNode = periodNode || '';
            
            // Handle multiple teachers - create separate records for each
            console.log(`🔍 Processing teachers:`, parsedTeachers);
            console.log(`🔍 Entry details:`, { subject: subjectName, class_id: classId, classroom: classroom });
            
            for (const teacherName of parsedTeachers) {
              if (!teacherName || teacherName.trim() === '') continue;
              
              console.log(`🔍 Looking up teacher: "${teacherName}"`);
              const correctTeacherId = await getTeacherIdByName(teacherName.trim());
              console.log(`📋 Teacher lookup result for "${teacherName}":`, correctTeacherId);
              
              if (correctTeacherId) {
                teacherIdsToDelete.add(correctTeacherId);
                const recordToAdd = {
              tenant_id: tenantId,
                  teacher_id: correctTeacherId,
              day_of_week: dayMapping[day],
                  period_sequence: periodSequence,
                  period_node: safePeriodNode,
                  period_number: periodNumber,
                  time_slot: safeTimeSlot,
                  subject: subjectName,
                  class_id: safeClassId,
                  classroom: safeClassroom,
                  teacher_name: safeTeacherName.trim(), // Use parsed teacher name, not logged-in user
                  subject_type: entry.subject_type || entry.type || 'core', // Add subject_type field from parser
                  elective_group: entry.elective_group || null, // Add elective_group field from parser
                  // co_teachers: entry.co_teachers ? JSON.stringify(entry.co_teachers) : null, // Temporarily disabled - column doesn't exist in DB
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString()
                };
                console.log(`✅ Adding record for ${teacherName}:`, recordToAdd);
                entriesWithTeacherIds.push(recordToAdd);
              } else {
                console.warn(`❌ Skipping entry for teacher "${teacherName}" - teacher not found in database`);
              }
            }
          }
        }
      }
      
      console.log('📊📊📊 DATA PROCESSING COMPLETE 📊📊📊');
      console.log('Total entries with teacher IDs:', entriesWithTeacherIds.length);
      console.log('Teacher IDs to delete:', Array.from(teacherIdsToDelete));
      console.log('📋 Sample entries being saved:', entriesWithTeacherIds.slice(0, 3));
      
      // Delete existing records for only the teachers that will be updated
      console.log('🗑️🗑️🗑️ STARTING DELETE OPERATION 🗑️🗑️🗑️');
      console.log(`🗑️ Teacher IDs to delete:`, Array.from(teacherIdsToDelete));
      if (teacherIdsToDelete.size > 0) {
        console.log(`🗑️ Deleting existing records for teachers: ${Array.from(teacherIdsToDelete).join(', ')}`);
        console.log('🚀🚀🚀 EXECUTING DELETE QUERY 🚀🚀🚀');
        const { error: deleteError } = await supabase
          .from('saas_teacher_timetables')
          .delete()
          .in('teacher_id', Array.from(teacherIdsToDelete))
          .eq('tenant_id', tenantId);

        console.log('📊📊📊 DELETE RESULT 📊📊📊');
        console.log('Delete error:', deleteError);
        if (deleteError) {
          console.error('❌ Error deleting existing records:', deleteError);
          showMessage('Error clearing existing timetable', 'error');
          return;
        } else {
          console.log('✅ Successfully deleted existing records');
        }
      } else {
        console.log('ℹ️ No teacher IDs to delete');
      }
      
      // Use the entries with correct teacher IDs
      newRecords.push(...entriesWithTeacherIds);

      console.log(`📊 Total records to save: ${newRecords.length}`);
      console.log('📋 All records to save:', JSON.stringify(newRecords, null, 2));
      
      if (newRecords.length === 0) {
        console.warn('⚠️ No records to save!');
        showMessage('No valid records to save', 'warning');
        return;
      }

      // Insert new records
      console.log('💾💾💾 STARTING INSERT OPERATION 💾💾💾');
      console.log('💾 Inserting records to database...');
      console.log('📋 Records to insert:', JSON.stringify(newRecords, null, 2));
      console.log('🚀🚀🚀 EXECUTING INSERT QUERY 🚀🚀🚀');
      console.log('📋 Records being inserted:', newRecords);
      
      // Check each field length before insertion
      newRecords.forEach((record, index) => {
        console.log(`🔍 Record ${index}:`, {
          subject: record.subject + ` (${record.subject?.length || 0} chars)`,
          class_id: record.class_id + ` (${record.class_id?.length || 0} chars)`,
          classroom: record.classroom + ` (${record.classroom?.length || 0} chars)`,
          teacher_name: record.teacher_name + ` (${record.teacher_name?.length || 0} chars)`,
          time_slot: record.time_slot + ` (${record.time_slot?.length || 0} chars)`,
          period_node: record.period_node + ` (${record.period_node?.length || 0} chars)`
        });
      });
      
      const { data: insertData, error: insertError } = await supabase
        .from('saas_teacher_timetables')
        .insert(newRecords)
        .select();

      console.log('📊📊📊 INSERT RESULT 📊📊📊');
      console.log('Insert data:', insertData);
      console.log('Insert error:', insertError);

      if (insertError) {
        console.error('❌❌❌ INSERT ERROR DETAILS ❌❌❌');
        console.error('Error code:', insertError.code);
        console.error('Error message:', insertError.message);
        console.error('Error details:', insertError.details);
        console.error('Error hint:', insertError.hint);
        showMessage('Error saving timetable to database: ' + insertError.message, 'error');
        return;
      } else {
        console.log('✅✅✅ INSERT SUCCESS ✅✅✅');
        console.log('Successfully inserted records:', insertData);
      }

      console.log('✅ SAVE COMPLETED SUCCESSFULLY!');
      showMessage('Timetable saved successfully!', 'success');
      setEditingMode(false);
      setEditableTimetable(null);
      setUploadedData(null);
      
      // Refresh the timetable data
      if (onTimetableUpdated) {
        onTimetableUpdated();
      }
    } catch (error) {
      console.error('Error saving timetable:', error);
      showMessage('Error saving timetable', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingMode(false);
    setEditableTimetable(null);
    setUploadedData(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const exportToJSON = async () => {
    try {
      setLoading(true);
      
      if (!editableTimetable || !editableTimetable.monday) {
        showMessage('No timetable data to export', 'error');
        return;
      }

      // Convert UI timetable data to export format
      const exportData = {
        metadata: {
          teacher_id: teacherId,
          teacher_name: teacherName,
          tenant_id: tenantId,
          export_date: new Date().toISOString(),
          academic_year: "2025-2026"
        },
        timetable: [],
        summary: {
          subjects_taught: [],
          classes_taught: [],
          total_teaching_periods: 0,
          total_empty_periods: 0,
          total_break_periods: 0
        }
      };

      // Process each day
      const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
      const dayMapping = {
        'monday': 'Monday',
        'tuesday': 'Tuesday',
        'wednesday': 'Wednesday',
        'thursday': 'Thursday',
        'friday': 'Friday'
      };

      days.forEach(day => {
        if (editableTimetable[day]) {
          editableTimetable[day].forEach((entry, index) => {
            // Skip null entries
            if (!entry) return;
            
            // Use the actual parsed data from the entry, not calculated values
            const periodSequence = entry.period_sequence || (index + 1);
            const periodNode = entry.period_node || calculatePeriodFields(periodSequence).periodNode;
            const periodNumber = entry.period_number !== undefined ? entry.period_number : calculatePeriodFields(periodSequence).periodNumber;
            const timeSlot = entry.time_slot || entry.time || getTimeSlotByPeriodSequence(periodSequence);
            
            exportData.timetable.push({
              day_of_week: dayMapping[day],
              time_slot: timeSlot,
              period_number: periodNumber,
              period_sequence: periodSequence,
              period_node: periodNode,
              subject: entry.subject || '',
              class_id: entry.class_id || 'ADMIN',
              classroom: entry.classroom || 'N/A',
              week_type: entry.week_type || 'regular',
              subject_type: entry.type || 'core',
              elective_group: entry.elective_group,
              notes: entry.notes || ''
            });

            // Update summary
            if (entry.subject && entry.subject !== 'Empty' && entry.subject !== '小息' && entry.subject !== '午膳') {
              if (!exportData.summary.subjects_taught.includes(entry.subject)) {
                exportData.summary.subjects_taught.push(entry.subject);
              }
              if (entry.class_id && entry.class_id !== 'ADMIN' && !exportData.summary.classes_taught.includes(entry.class_id)) {
                exportData.summary.classes_taught.push(entry.class_id);
              }
              exportData.summary.total_teaching_periods++;
            } else if (entry.subject === 'Empty') {
              exportData.summary.total_empty_periods++;
            } else if (entry.subject === '小息' || entry.subject === '午膳') {
              exportData.summary.total_break_periods++;
            }
          });
        }
      });

      exportData.metadata.total_periods = exportData.timetable.length;

      // Create and download JSON file
      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `timetable_${teacherName}_${teacherId}_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showMessage(`Timetable exported successfully for ${teacherName}`, 'success');
      
    } catch (error) {
      console.error('Error exporting timetable:', error);
      showMessage('Error exporting timetable', 'error');
    } finally {
      setLoading(false);
    }
  };

  const importFromJSON = async (event) => {
    try {
      setLoading(true);
      
      const file = event.target.files[0];
      if (!file) {
        showMessage('No file selected', 'error');
        return;
      }

      const fileContent = await file.text();
      const importData = JSON.parse(fileContent);

      // Validate JSON structure
      if (!importData.metadata || !importData.timetable) {
        showMessage('Invalid JSON file format', 'error');
        return;
      }

      // Delete existing records
      const { error: deleteError } = await supabase
        .from('saas_teacher_timetables')
        .delete()
        .eq('teacher_id', teacherId)
        .eq('tenant_id', tenantId);

      if (deleteError) {
        console.error('Error deleting existing records:', deleteError);
        showMessage('Error clearing existing timetable', 'error');
        return;
      }

      // Insert new records
      const newRecords = importData.timetable.map(entry => ({
        tenant_id: tenantId,
        teacher_id: teacherId,
        teacher_name: teacherName,
        day_of_week: entry.day_of_week,
        time_slot: entry.time_slot,
        period_number: entry.period_number,
        subject: entry.subject,
        class_id: entry.class_id,
        classroom: entry.classroom,
        week_type: entry.week_type || 'regular',
        subject_type: entry.subject_type,
        elective_group: entry.elective_group,
        notes: entry.notes
      }));

      const { error: insertError } = await supabase
        .from('saas_teacher_timetables')
        .insert(newRecords);

      if (insertError) {
        console.error('Error inserting records:', insertError);
        showMessage('Error importing timetable data', 'error');
        return;
      }

      showMessage(`Timetable imported successfully for ${teacherName}`, 'success');
      
      // Notify parent component to refresh data
      if (onTimetableUpdated) {
        onTimetableUpdated();
      }
      
    } catch (error) {
      console.error('Error importing timetable:', error);
      showMessage('Error importing timetable file', 'error');
    } finally {
      setLoading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  // REMOVED: Duplicate save function - using handleSaveEditedTimetable instead
  const saveToDatabase_DISABLED = async () => {
    console.log('🔥🔥🔥 SAVE BUTTON CLICKED! 🔥🔥🔥');
    console.log('🔍 Current editableTimetable state:', editableTimetable);
    console.log('🔍 editableTimetable type:', typeof editableTimetable);
    console.log('🔍 editableTimetable keys:', editableTimetable ? Object.keys(editableTimetable) : 'N/A');
    
    try {
      setLoading(true);
      
      if (!editableTimetable || !editableTimetable.monday) {
        console.log('❌ No editableTimetable data or monday data');
        showMessage('No timetable data to save', 'error');
        return;
      }

      // Convert UI data to database format
      const newRecords = [];
      const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
      const dayMapping = {
        'monday': 'Monday',
        'tuesday': 'Tuesday',
        'wednesday': 'Wednesday',
        'thursday': 'Thursday',
        'friday': 'Friday'
      };

      for (const day of days) {
        if (editableTimetable[day]) {
          for (let index = 0; index < editableTimetable[day].length; index++) {
            const entry = editableTimetable[day][index];
            
            // Skip null entries
            if (!entry) continue;
            
            // Use the actual parsed data from the entry, not calculated values
            const periodSequence = entry.period_sequence || (index + 1);
            const periodNode = entry.period_node || calculatePeriodFields(periodSequence).periodNode;
            const periodNumber = entry.period_number !== undefined ? entry.period_number : calculatePeriodFields(periodSequence).periodNumber;
            const timeSlot = entry.time_slot || entry.time || getTimeSlotByPeriodSequence(periodSequence);
            
            console.log(`Saving entry for ${day} at index ${index}: period_sequence=${periodSequence}, period_node=${periodNode}, period_number=${periodNumber}, time_slot=${timeSlot}`, entry);
            
            // Extract class information from the grid data
            const classId = entry.class_id || '';
            const classroom = entry.classroom || '';
            const teacherName = entry.teacher || '';
            
            // Get the correct teacher ID for this entry
            console.log(`🔍 Looking up teacher: "${teacherName}"`);
            const correctTeacherId = await getTeacherIdByName(teacherName);
            console.log(`📋 Teacher lookup result for "${teacherName}":`, correctTeacherId);
            
            if (correctTeacherId) {
            newRecords.push({
              tenant_id: tenantId,
                teacher_id: correctTeacherId,
              day_of_week: dayMapping[day],
                period_sequence: periodSequence,
                period_node: periodNode,
                period_number: periodNumber,
                time_slot: timeSlot,
                subject: entry.subject || '',
                class_id: classId,
                classroom: classroom,
                teacher_name: teacherName,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              });
              console.log(`✅ Added record for teacher "${teacherName}" with ID "${correctTeacherId}"`);
            } else {
              console.warn(`❌ Skipping entry for teacher "${teacherName}" - teacher not found in database`);
            }
          }
        }
      }

      // Insert new records
      const { error: insertError } = await supabase
        .from('saas_teacher_timetables')
        .insert(newRecords);

      if (insertError) {
        console.error('Error inserting records:', insertError);
        showMessage('Error saving timetable to database', 'error');
        return;
      }

      showMessage(`Timetable saved successfully for ${teacherName}`, 'success');
      
      // Notify parent component to refresh data
      if (onTimetableUpdated) {
        onTimetableUpdated();
      }
      
    } catch (error) {
      console.error('Error saving timetable:', error);
      showMessage('Error saving timetable', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="timetable-management bg-white rounded-lg shadow-md p-6 mb-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
        <span className="mr-2">⚙️</span>
        Timetable Management
      </h3>
      
      {/* Setup Buttons */}
      <div className="setup-toggle-buttons">
        <button
          onClick={() => setShowAppSetup(!showAppSetup)}
          className="setup-toggle-btn app-setup"
        >
          <span>{showAppSetup ? '👁️‍🗨️' : '👁️'}</span>
          {showAppSetup ? 'Hide App Setup' : 'Show App Setup'}
        </button>
        <button
          onClick={() => setShowSetup(!showSetup)}
          className="setup-toggle-btn timetable-setup"
        >
          <span>{showSetup ? '👁️‍🗨️' : '👁️'}</span>
          {showSetup ? 'Hide Timetable Setup' : 'Show Timetable Setup'}
        </button>
        <button
          onClick={() => setShowDebug(!showDebug)}
          className="setup-toggle-btn debug-data"
        >
          <span>{showDebug ? '👁️‍🗨️' : '👁️'}</span>
          {showDebug ? 'Hide Debug Data' : 'Show Debug Data'}
        </button>
        <button
          onClick={() => setShowSettings(!showSettings)}
          className="setup-toggle-btn settings"
        >
          <span>{showSettings ? '⚙️' : '⚙️'}</span>
          {showSettings ? 'Hide Settings' : 'Show Settings'}
        </button>
      </div>

      {/* App Setup Manager Component */}
      {showAppSetup && (
        <AppSetupManager onSetupComplete={handleAppSetupComplete} />
      )}

      {/* Timetable Setup Component */}
      {showSetup && (
        <TimetableSetup onSetupComplete={handleSetupComplete} />
      )}

      {/* Debug Data Viewer Component - Removed (Debug components deleted during migration) */}
      {showDebug && (
        <div className="space-y-6">
          <p>Debug components have been removed. Debug features are not available in the teacher app.</p>
        </div>
      )}

      {/* Timetable Settings Component */}
      {showSettings && (
        <TimetableSettings />
      )}
      
      {/* File Upload Section */}
      {!editingMode && (
        <div className="mb-6 p-4 border border-gray-300 rounded-lg bg-gray-50">
          <h4 className="font-medium text-gray-700 mb-3">📁 Script File Upload</h4>
          <p className="text-sm text-gray-600 mb-3">
            上傳 .txt 格式的時間表檔案 (例如: 01 陳嘉俊.txt) 並使用多解析技術處理多班別、多教師、多課室的情況
          </p>
          {!parser && (
            <div className="mb-3 p-2 bg-yellow-100 border border-yellow-300 rounded text-sm text-yellow-700">
              ⚠️ Parser not initialized. Please configure timetable setup first.
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".txt"
            onChange={handleFileUpload}
            disabled={!parser}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100 disabled:opacity-50"
          />
        </div>
      )}

      {/* Editing Mode Controls */}
      {console.log('🔍 editingMode state:', editingMode, 'teacherName:', teacherName)}
      {editingMode && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <span className="text-blue-600">✏️</span>
              <span className="font-medium text-gray-800">編輯模式 - {teacherName}</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-xs text-gray-600">即時編輯</span>
            </div>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={() => {
                console.log('🟢🟢🟢 SAVE BUTTON CLICKED! 🟢🟢🟢');
                console.log('🟢 Button click detected - calling handleSaveEditedTimetable');
                console.log('🔍 Loading state:', loading);
                handleSaveEditedTimetable();
              }}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              style={{ backgroundColor: loading ? '#9CA3AF' : '#10B981' }}
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>儲存中...</span>
                </>
              ) : (
                <>
                  <span>💾</span>
                  <span>儲存時間表</span>
                </>
              )}
            </button>
            
            <button
              onClick={handleCancelEdit}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              <span>❌</span>
              <span>取消編輯</span>
            </button>
          </div>
        </div>
      )}

      {/* Timetable Grid */}
      {editingMode && editableTimetable && (
        <div className="mb-6">
          <TimetableGrid 
            data={editableTimetable}
            title="編輯時間表 - 全屏顯示版本"
            hasElectives={false}
            editable={true}
            onCellEdit={handleCellEdit}
            tenantId={tenantId}
          />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Export to JSON */}
        <button
          onClick={exportToJSON}
          disabled={loading}
          className="flex items-center justify-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? (
            <span className="flex items-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Exporting...
            </span>
          ) : (
            <span className="flex items-center">
              <span className="mr-2">📤</span>
              Export JSON
            </span>
          )}
        </button>

        {/* Import from JSON */}
        <label className="flex items-center justify-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 cursor-pointer transition-colors">
          <span className="mr-2">📥</span>
          Import JSON
          <input
            type="file"
            accept=".json"
            onChange={importFromJSON}
            disabled={loading}
            className="hidden"
          />
        </label>

      </div>

      {/* Message Display */}
      {message && (
        <div className={`mt-4 p-3 rounded-lg ${
          messageType === 'success' ? 'bg-green-100 text-green-800 border border-green-200' :
          messageType === 'error' ? 'bg-red-100 text-red-800 border border-red-200' :
          'bg-blue-100 text-blue-800 border border-blue-200'
        }`}>
          <div className="flex items-center">
            <span className="mr-2">
              {messageType === 'success' ? '✅' : messageType === 'error' ? '❌' : 'ℹ️'}
            </span>
            {message}
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-gray-700 mb-2">Instructions:</h4>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• <strong>Timetable Setup:</strong> Configure weekday/cycle mode, day periods, and period patterns first</li>
          <li>• <strong>Script File Upload:</strong> Upload .txt timetable files with enhanced multi-parsing support</li>
          <li>• <strong>Edit Mode:</strong> After uploading, edit each cell in the timetable before saving</li>
          <li>• <strong>SAVE Script:</strong> Save the edited timetable to Supabase database</li>
          <li>• <strong>Export JSON:</strong> Download current timetable as JSON file</li>
          <li>• <strong>Import JSON:</strong> Upload and apply timetable from JSON file</li>
          <li>• <strong>Save to Database:</strong> Save current timetable data to Supabase</li>
        </ul>
        <div className="mt-3 p-3 bg-blue-50 rounded border border-blue-200">
          <p className="text-sm text-blue-700">
            <strong>Enhanced Multi-Parsing Features:</strong> The system now supports complex parsing patterns like "4AB3 英國語文 110 藍楚喬,任康哲" 
            and "3B/3D 基本商業(實習) 112/510 余潔珍", automatically creating separate records for each class/teacher/room combination.
          </p>
        </div>
        <div className="mt-2 p-3 bg-green-50 rounded border border-green-200">
          <p className="text-sm text-green-700">
            <strong>Period Management:</strong> Configure period sequences and nodes (班主任節, 小息, 午膳, Period 1-9) 
            with proper time slots for accurate timetable display.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TimetableManagement;
